import { useMemo } from 'react'
import { IntersectionOptions, useInView } from 'react-intersection-observer'

import dayjs from 'dayjs'
import { get } from 'lodash'

import { ApolloClient } from '@apollo/client'

import { globalBoostrapPropsWrapper } from '~/utils/globalBootstrap'
import { dayjsToString } from '~/utils/graphqlDataFormatters'
import { isFER } from '~/utils/isReservableType'
import { RESERVABLES_SEARCH_DAYS_COUNT } from '~/utils/reservables'

import { getClubConfig } from '~/config/clubConfig'
import { paths } from '~/constants'
import { ReservablesDocument, ReservablesQuery, Restaurant, SingleRestaurantDocument, SingleRestaurantQuery } from '~/generated/graphql'

export const getRestaurantPageServerSideProps = async <AdditionalProps extends Record<string, unknown>>({
  restaurantId,
  apolloClient,
  additionalProps,
}: {
  restaurantId?: Restaurant['id']
  apolloClient: ApolloClient<any>
  additionalProps?: AdditionalProps
}) => {
  if (!restaurantId) {
    return {
      redirect: {
        destination: paths.error404,
        permanent: true,
      },
    }
  }

  const {
    data: { restaurant },
  } = await apolloClient.query<SingleRestaurantQuery>({
    query: SingleRestaurantDocument,
    variables: { id: restaurantId },
  })

  const now = dayjs()

  await apolloClient.query<ReservablesQuery>({
    query: ReservablesDocument,
    variables: {
      restaurantId: restaurantId,
      dates: {
        startsOn: dayjsToString(now),
        endsOn: dayjsToString(now.clone().add(RESERVABLES_SEARCH_DAYS_COUNT, 'day')),
      },
    },
  })

  const festivalRedirect = getClubConfig().redirectRestaurantPageToListing
  if (festivalRedirect) {
    // we redirect to listing with FER modal
    const fer = restaurant?.reservables?.filter(isFER).find(fer => fer.festivalEdition.festival.code === festivalRedirect)

    // if no FER was found redirect to homepage
    if (!fer) {
      return {
        redirect: {
          destination: paths.home,
          permanent: false,
        },
      }
    }

    return {
      redirect: {
        destination: paths.festivalListing(fer.festivalEdition.festival.slug, fer.id),
        permanent: false,
      },
    }
  }

  if (!restaurant) {
    return {
      redirect: {
        destination: '/festivals',
        permanent: false,
      },
    }
  }

  const name = get(additionalProps, 'query.name', null)

  return globalBoostrapPropsWrapper(
    { apolloClient },
    {
      props: {
        restaurant,
        restaurantId,
        defaultCollapse: name,
        ...additionalProps,
        // similarRestaurants,
      },
    }
  )
}

export const getCollapseData = (name: string) => {
  switch (name) {
    case 'fine-dining-week':
      return { name: 'FineDiningWeek® menu reservation', description: 'Signature 3 or 5-course menu + festival cocktail' }
    case 'restaurant-week':
      return { name: 'RestaurantWeek® menu reservation', description: 'Signature 3 courses + cocktail at the festival price' }
    case 'breakfast-week':
      return { name: 'BreakfastWeek menu reservation', description: 'Signature breakfast + plant-based coffee at a festival price' }
    case 'ginweek':
      return { name: 'GinWeek menu reservation', description: 'The best bars, sets of 2 cocktails' }
    case 'chm':
      return { name: "Make a reservation with Chef's flagship menu", description: 'Discover this restaurant at a bargain price' }
    case 'daily':
    default:
      return { name: 'Book just a table', description: 'Make a reservation without the signature menu' }
  }
}

const inViewOptions: IntersectionOptions = {
  delay: 50,
  rootMargin: '-184px 0px 0px 0px',
}

export const useRestaurantPageTabs = () => {
  const { ref: menuRef, inView: menuInView } = useInView(inViewOptions)
  const { ref: descriptionRef, inView: descriptionInView } = useInView(inViewOptions)
  const { ref: featuresRef, inView: featuresInView } = useInView(inViewOptions)
  const { ref: locationRef, inView: locationInView } = useInView(inViewOptions)

  const activeTab = useMemo(
    () =>
      [
        { id: 'menu', inView: menuInView },
        { id: 'description', inView: descriptionInView },
        { id: 'features', inView: featuresInView },
        { id: 'location', inView: locationInView },
      ].find(({ inView }) => inView),
    [descriptionInView, featuresInView, locationInView, menuInView]
  )

  return useMemo(
    () => ({
      tabRefs: { menuRef, descriptionRef, featuresRef, locationRef },
      activeTab,
    }),
    [activeTab, descriptionRef, featuresRef, locationRef, menuRef]
  )
}
